import * as React from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { Thumb } from "./thumb";
import { cn } from "@shared/lib/cn";
import { useMergedRef } from "./merge-ref";

/*
 * Меры набора v6 (`SZ.toggle` в `liquid-ui.js`): ширина и высота дорожки,
 * шарик в покое, шарик в руке. В приложении стоит `s`; `m` и `l` — для мест
 * крупнее.
 */
const SIZES = {
  s: [46, 24, 24, 20, 36, 30],
  m: [58, 30, 34, 26, 50, 38],
  l: [70, 36, 40, 32, 60, 46],
} as const;

/**
 * Переключатель по набору v6.
 *
 * Шарик — белая капсула шире своей высоты. Нажали — он пружиной растёт,
 * едет за пальцем и тянется на скорости (стеклянной линзы, как в макете, нет —
 * вариант «В» из проб без стекла); отпустили — переключатель встаёт на ту
 * сторону, где шарик оставили, а короткое нажатие просто переключает.
 * Включённое положение заливается по мере хода шарика, а не щелчком в конце.
 * Движения целиком — в `Thumb` (`shared/ui/thumb.ts`).
 *
 * Объявляется переключателем (`role="switch"`), а не отметкой: слышно то
 * же, что видно. Кнопка, а не блок, — чтобы подпись вокруг (`<label>`)
 * переключала его щелчком, как и прежде.
 *
 * Значение держит хозяин (`checked`). Не принял смену — например, система
 * не дала разрешения на оповещения, — шарик вернётся туда, где был.
 */
function Switch({
  checked = false,
  onCheckedChange,
  disabled = false,
  size = "s",
  className,
  ref,
  ...rest
}: Omit<React.ComponentProps<"button">, "onChange" | "value"> & {
  checked?: boolean;
  onCheckedChange?: (next: boolean) => void;
  size?: keyof typeof SIZES;
}) {
  const [TW, TH, KW, KH, HW, HH] = SIZES[size];
  const root = useRef<HTMLButtonElement | null>(null);
  const setRoot = useMergedRef(root, ref);
  const on = useRef<HTMLSpanElement>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const knob = useRef<HTMLSpanElement>(null);
  const control = useRef<Thumb | null>(null);
  const latest = useRef({ checked, onCheckedChange });

  useLayoutEffect(() => {
    latest.current = { checked, onCheckedChange };
  });

  useLayoutEffect(() => {
    const c = new Thumb({
      kind: "toggle",
      root: root.current!,
      knob: knob.current!,
      wraps: [wrap.current!],
      rest: [KW, KH],
      hold: [HW, HH],
      val: latest.current.checked ? 1 : 0,
      onPaint: (fx) => {
        on.current!.style.opacity = fx.toFixed(3);
      },
      onChange: (v) => {
        latest.current.onCheckedChange?.(!!v);
        requestAnimationFrame(() => control.current?.set(latest.current.checked ? 1 : 0));
      },
    });
    control.current = c;
    return () => {
      c.destroy();
      control.current = null;
    };
  }, [KW, KH, HW, HH]);

  useEffect(() => {
    control.current?.set(checked ? 1 : 0);
  }, [checked]);

  useEffect(() => {
    control.current?.setDisabled(disabled);
  }, [disabled]);

  return (
    <button
      ref={setRoot}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      data-slot="switch"
      data-state={checked ? "checked" : "unchecked"}
      className={cn("lui-toggle", className)}
      style={{ width: TW, height: TH }}
      {...rest}
    >
      <span className="lui-toggle-on" ref={on} />
      <span className="lui-wrap" ref={wrap}>
        <span className="lui-knob" ref={knob} style={{ width: KW, height: KH }} />
      </span>
    </button>
  );
}

export { Switch };
